/**
 * Tendril trace utilities.
 *
 * Provides W3C `traceparent` header generation for Tendril's per-session
 * identifier. This is SEPARATE from the existing tracing.js (which uses
 * Sentry's active span). The traceparent generated here is used as a
 * stable session-scoped identifier sent in request bodies.
 *
 * W3C traceparent format: `00-{32hex traceId}-{16hex spanId}-01`
 * Reference: https://www.w3.org/TR/trace-context/
 *
 * Note: `generateTraceparent` is called per-request for request-level
 * tracing. `getSessionTraceparent` generates once and caches in a module
 * closure for the lifetime of the browser session (useful for conversationId
 * correlation).
 */

/**
 * Generate a random hex string of the given byte length.
 * Uses crypto.getRandomValues for cryptographically-random output.
 *
 * @param {number} byteLength - Number of bytes (output length = byteLength * 2 hex chars)
 * @returns {string} Lowercase hex string
 */
function randomHex(byteLength) {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate a fresh W3C traceparent string.
 * Format: `00-{32hex}-{16hex}-01`
 *
 * @returns {string} W3C traceparent string
 */
export function generateTraceparent() {
  const traceId = randomHex(16); // 128 bits = 32 hex chars
  const spanId = randomHex(8);   // 64 bits = 16 hex chars
  return `00-${traceId}-${spanId}-01`;
}

// Module-level singleton for session-scoped traceparent
let _sessionTraceparent = null;

/**
 * Get or generate a stable per-session traceparent.
 * Generated once on first call; same value returned on subsequent calls.
 *
 * @returns {string} W3C traceparent string (stable per page session)
 */
export function getSessionTraceparent() {
  if (_sessionTraceparent === null) {
    _sessionTraceparent = generateTraceparent();
  }
  return _sessionTraceparent;
}
